import { resolveAttachmentUrlCached } from '../../utils/attachment-url-cache'
import { getNativePlatform } from '../../utils/platform'
import {
  DIAL_TONE_REPEAT_COUNT,
  dialToneHangupTimeoutMs,
  pickDialToneSlot,
  type DialToneOrgConfig,
  type DialToneSlot,
} from '@shared/dial-tones/types'
import {
  getCachedOrgDialToneConfigState,
  refreshOrgDialToneConfig,
} from './dial-tone-org-service'

export type OutgoingDialPlan = {
  /** Resolved playable URL (presigned or bundled). Null → built-in dial-tone.wav */
  playUrl: string | null
  /** Caller: play N full takes then hang up. */
  repeatCount: number
  hangupTimeoutMs: number
  slot: DialToneSlot | null
}

const DEFAULT_RING_TIMEOUT_MS = 45_000

/**
 * Android's CallAnomalyWatchdog disconnects a self-managed call still DIALING
 * after 120 s (SDK 35+ targets). An org dial tone longer than ~40 s makes the
 * 3-play timeout exceed that (up to 182 s), so the watchdog — not our MISSED
 * END — ended the dial. Android dials give up at 115 s; iOS / web keep the
 * tone-length timeout. Native backstop: AscendConnection.DIALING_STALL_TEARDOWN_MS.
 */
export const ANDROID_OUTGOING_NO_ANSWER_CAP_MS = 115_000

export function capOutgoingNoAnswerTimeoutMs(
  timeoutMs: number,
  platform: 'ios' | 'android' | 'web',
): number {
  return platform === 'android' ? Math.min(timeoutMs, ANDROID_OUTGOING_NO_ANSWER_CAP_MS) : timeoutMs
}

function hangupTimeoutFor(slot: DialToneSlot | null, fallbackTimeoutMs: number): number {
  return capOutgoingNoAnswerTimeoutMs(
    dialToneHangupTimeoutMs(slot, fallbackTimeoutMs),
    getNativePlatform(),
  )
}

let activePlan: OutgoingDialPlan | null = null

export function getActiveOutgoingDialPlan(): OutgoingDialPlan | null {
  return activePlan
}

export function clearActiveOutgoingDialPlan(): void {
  activePlan = null
}

/**
 * SYNC cache of resolved playable URLs, keyed by slot attachment key.
 *
 * Why (2026-08 "previous tone plays then the new one cuts in"): tone playback
 * used to always start with the BUILT-IN asset and force-swap to the org tone
 * once the async resolve landed — an audible, jarring mid-ring swap on EVERY
 * call. With the URLs pre-warmed here, the org tone starts on the very first
 * tick and no swap ever happens. TTL sized to the presigned-URL lifetime
 * (~1h) with margin; past the TTL we fall back to start-builtin-then-swap.
 * (2026-08-18: was 5min — combined with the 60s org-config freshness window
 * it made the old-tone flash reappear on any call placed a few minutes after
 * the previous one.)
 */
const SYNC_URL_TTL_MS = 45 * 60_000
const resolvedUrlCache = new Map<string, { url: string; expiresAtMs: number }>()

function rememberResolvedUrl(attachmentUrl: string, url: string | null): void {
  if (!url) return
  resolvedUrlCache.set(attachmentUrl, { url, expiresAtMs: Date.now() + SYNC_URL_TTL_MS })
}

function getResolvedUrlSync(attachmentUrl: string | null | undefined): string | null {
  if (!attachmentUrl) return null
  const hit = resolvedUrlCache.get(attachmentUrl)
  if (!hit || Date.now() >= hit.expiresAtMs) return null
  return hit.url
}

async function resolvePlayUrl(slot: DialToneSlot | null): Promise<string | null> {
  if (!slot) return null
  try {
    const url = await resolveAttachmentUrlCached(slot.attachmentUrl)
    rememberResolvedUrl(slot.attachmentUrl, url)
    return url
  } catch {
    return null
  }
}

/**
 * Refresh the org config + re-resolve every tone URL in the background,
 * de-duplicated so a burst of calls doesn't stack listUsers round-trips.
 * Keeps the sync caches perpetually warm once the app has loaded them once —
 * the stale-while-revalidate half of the "old tone flashes in" fix.
 */
let revalidateInFlight: Promise<void> | null = null
function revalidateDialToneCachesInBackground(): void {
  if (revalidateInFlight) return
  revalidateInFlight = (async () => {
    try {
      await refreshOrgDialToneConfig()
      await warmDialToneAssets()
    } catch {
      /* best-effort — stale caches keep serving */
    } finally {
      revalidateInFlight = null
    }
  })()
}

/**
 * Build the outgoing dial plan synchronously from warmed caches (org config +
 * resolved URLs). Returns null when anything is cold — callers then use the
 * async {@link prepareOutgoingDialPlan} with the built-in tone as a stopgap.
 * On success this ALSO installs the active plan (hangup timeout consumers).
 *
 * A STALE (but previously loaded) org config is still served — org tones
 * change rarely and starting the built-in only to audibly swap seconds later
 * is far worse than playing a possibly-minutes-old org tone. A background
 * revalidation is kicked so the caches converge.
 */
export function getSyncOutgoingDialPlan(
  mediaType: 'voice' | 'video',
  callId: string,
  fallbackTimeoutMs: number = DEFAULT_RING_TIMEOUT_MS,
): OutgoingDialPlan | null {
  const state = getCachedOrgDialToneConfigState()
  if (!state.loaded) return null
  if (state.stale) revalidateDialToneCachesInBackground()
  const section = mediaType === 'video' ? state.config?.videoDial : state.config?.voiceDial
  const slot = section ? pickDialToneSlot(section, callId) : null
  if (!slot) {
    // Org genuinely has no tone for this media type: the built-in IS the plan.
    const plan: OutgoingDialPlan = {
      playUrl: null,
      repeatCount: 0,
      hangupTimeoutMs: hangupTimeoutFor(null, fallbackTimeoutMs),
      slot: null,
    }
    activePlan = plan
    return plan
  }
  const playUrl = getResolvedUrlSync(slot.attachmentUrl)
  if (!playUrl) return null
  const plan: OutgoingDialPlan = {
    playUrl,
    repeatCount: DIAL_TONE_REPEAT_COUNT,
    hangupTimeoutMs: hangupTimeoutFor(slot, fallbackTimeoutMs),
    slot,
  }
  activePlan = plan
  return plan
}

/** Last resolved connecting-tone URL, if still fresh (sync, for instant start). */
export function getCachedConnectingToneUrl(): string | null {
  const state = getCachedOrgDialToneConfigState()
  if (!state.loaded) return null
  if (state.stale) revalidateDialToneCachesInBackground()
  const slot = state.config?.connecting.slot ?? null
  if (!slot) return null
  return getResolvedUrlSync(slot.attachmentUrl)
}

/**
 * Pre-resolve every org tone (dial slots + connecting) into the sync cache so
 * call-time playback starts with the right asset on the FIRST tick. Runs at
 * controller mount and is cheap to repeat (attachment resolutions are cached).
 */
export async function warmDialToneAssets(): Promise<void> {
  try {
    const config = await loadOrgConfig()
    if (!config) return
    const slots: DialToneSlot[] = [
      ...config.voiceDial.slots,
      ...config.videoDial.slots,
      ...(config.connecting.slot ? [config.connecting.slot] : []),
    ]
    await Promise.all(slots.map((slot) => resolvePlayUrl(slot)))
  } catch {
    /* warming is best-effort — call-time resolution still works */
  }
}

async function loadOrgConfig(): Promise<DialToneOrgConfig | null> {
  // Distinguish "never loaded" from "loaded empty / no org tones" so the
  // connecting-tone path does not await listUsers on every answer. A stale
  // config is served immediately (stale-while-revalidate) — awaiting a
  // listUsers round-trip here is what used to delay the org tone and cause
  // the audible built-in → org swap.
  const state = getCachedOrgDialToneConfigState()
  if (state.loaded) {
    if (state.stale) revalidateDialToneCachesInBackground()
    return state.config
  }
  return refreshOrgDialToneConfig()
}

/**
 * Pick voice/video dial slot for this call and cache the plan for hangup
 * timeout + caller ringback playback. Pass `callId` so retries / multi-device
 * resolve the same random slot.
 */
export async function prepareOutgoingDialPlan(
  mediaType: 'voice' | 'video',
  fallbackTimeoutMsOrOptions:
    | number
    | { fallbackTimeoutMs?: number; callId?: string } = DEFAULT_RING_TIMEOUT_MS,
): Promise<OutgoingDialPlan> {
  const options =
    typeof fallbackTimeoutMsOrOptions === 'number'
      ? { fallbackTimeoutMs: fallbackTimeoutMsOrOptions }
      : fallbackTimeoutMsOrOptions
  const fallbackTimeoutMs = options.fallbackTimeoutMs ?? DEFAULT_RING_TIMEOUT_MS
  const config = await loadOrgConfig()
  const section = mediaType === 'video' ? config?.videoDial : config?.voiceDial
  const slot = section
    ? pickDialToneSlot(section, options.callId ?? Math.random)
    : null
  const playUrl = await resolvePlayUrl(slot)
  const plan: OutgoingDialPlan = {
    playUrl,
    repeatCount: playUrl ? DIAL_TONE_REPEAT_COUNT : 0,
    hangupTimeoutMs: hangupTimeoutFor(slot, fallbackTimeoutMs),
    slot,
  }
  activePlan = plan
  return plan
}

export async function resolveConnectingToneUrl(): Promise<string | null> {
  const config = await loadOrgConfig()
  const slot = config?.connecting.slot ?? null
  if (!slot) return null
  try {
    const url = await resolveAttachmentUrlCached(slot.attachmentUrl)
    rememberResolvedUrl(slot.attachmentUrl, url)
    return url
  } catch {
    return null
  }
}
